import { ReactComponent as Spinner } from "assets/images/spinner-s.svg"
import { useEffect, useState } from "react"
import { SURVEY_CONFIG_UGC44 } from "config/ugc44"
import {
  amplitudeClient,
  sendEventsEvenWhenPageCloses,
  sendEventsNormally,
} from "lib/amplitude"
import { getCompletedStepId, saveCompletedSteps } from "lib/localStorage"
import { Survey } from "../survey"

const SURVEY_ID = "ugc44"
const EXPERIMENT_NAME = "w2a_ugc44_promocode_exp"
const NO_QUIZ_VARIANT = "no_quiz"
const VARIANT_OVERRIDE_PARAM = "ab_variant"
const PROMO_CODE_PARAM = "promo_code"
const VARIANT_PARAM_FOR_WEB_APP = "utm_content"

const WEB_APP_DISCOUNT_URL =
  process.env.REACT_APP_ENVIROMENT === "testing"
    ? "https://pw-testing.stage-thepure.app/app/en/discount"
    : "https://pure.app/app/en/discount"

const buildWebAppDiscountUrl = (
  funnelSearch: string,
  experimentVariant: string | null,
) => {
  const funnelParams = new URLSearchParams(funnelSearch)
  const url = new URL(WEB_APP_DISCOUNT_URL)
  const promoCode = funnelParams.get(PROMO_CODE_PARAM)

  if (promoCode) {
    url.searchParams.set(PROMO_CODE_PARAM, promoCode)
  }
  funnelParams.forEach((value, key) => {
    if (key.startsWith("utm_")) {
      url.searchParams.set(key, value)
    }
  })
  if (experimentVariant) {
    url.searchParams.set(VARIANT_PARAM_FOR_WEB_APP, experimentVariant)
  }

  return url.toString()
}

const fetchExperimentVariant = async () => {
  try {
    await amplitudeClient.fetchExperimentOptions([EXPERIMENT_NAME])
    // This comes before the variant call, so that the exposure event it logs is
    // still sent when variant 2 leaves the page straight away.
    sendEventsEvenWhenPageCloses()
    const variant =
      await amplitudeClient.fetchExperimentVariant(EXPERIMENT_NAME)
    return variant?.value ?? null
  } catch {
    return null
  }
}

const keepSavedStepInside = () => {
  const lastStepIndex = SURVEY_CONFIG_UGC44.flat().length - 1
  if (getCompletedStepId(SURVEY_ID) > lastStepIndex) {
    saveCompletedSteps(SURVEY_ID, lastStepIndex)
  }
}

export function Ugc44Gate() {
  const [showQuiz, setShowQuiz] = useState(false)
  const [experimentVariant, setExperimentVariant] = useState<string | null>(
    null,
  )

  useEffect(() => {
    let active = true

    const chooseVariant = async () => {
      const variantOverride = new URLSearchParams(window.location.search).get(
        VARIANT_OVERRIDE_PARAM,
      )
      const fetchedVariant = variantOverride
        ? null
        : await fetchExperimentVariant()
      const variant = variantOverride ?? fetchedVariant

      if (!active) return

      if (variant === NO_QUIZ_VARIANT) {
        window.location.replace(
          buildWebAppDiscountUrl(window.location.search, fetchedVariant),
        )
        return
      }

      sendEventsNormally()
      keepSavedStepInside()
      setExperimentVariant(fetchedVariant)
      setShowQuiz(true)
    }

    chooseVariant()

    return () => {
      active = false
    }
  }, [])

  if (!showQuiz) {
    return (
      <div className="max-w-[450px] mx-auto pt-[72px] px-4 pb-10 flex items-center justify-center h-full">
        <Spinner className="animate-spin text-black" />
      </div>
    )
  }

  return (
    <Survey
      surveyId={SURVEY_ID}
      steps={SURVEY_CONFIG_UGC44}
      onFinish={() =>
        window.location.assign(
          buildWebAppDiscountUrl(window.location.search, experimentVariant),
        )
      }
    />
  )
}
